import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const DEFAULTS = {
  brand_name: 'AwadhPay',
  short_name: 'AwadhPay',
  description: 'Enterprise payment infrastructure for modern businesses.',
  primary_logo_url: null,
  light_logo_url: null,
  dark_logo_url: null,
  favicon_url: null,
  app_icon_url: null,
  meta_title: 'AwadhPay | Enterprise Payin & Payout Infrastructure',
  meta_description: 'AwadhPay provides enterprise Payin and Payout infrastructure for modern businesses, with secure payment processing, merchant wallets, transaction management and scalable payment operations.',
  support_email: 'support@awadhpay.com',
};

const BrandingContext = createContext(null);

export const useBranding = () => {
  const ctx = useContext(BrandingContext);
  return ctx || { branding: DEFAULTS, assetUrl: (u) => u, refreshBranding: () => {} };
};

// Convert a backend-relative asset path (e.g. /api/platform/assets/...) into an absolute URL.
export const toAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const setMeta = (selector, attr, key, value) => {
  if (!value) return;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const applyDocumentBranding = (b) => {
  if (b.meta_title) document.title = b.meta_title;

  const desc = b.meta_description;
  if (desc) {
    setMeta('meta[name="description"]', 'name', 'description', desc);
  }

  // Keep Open Graph / Twitter metadata in sync (AwadhPay branding only)
  const ogTitle = b.og_title || b.meta_title;
  const ogDesc = b.og_description || b.meta_description;
  setMeta('meta[property="og:title"]', 'property', 'og:title', ogTitle);
  setMeta('meta[property="og:description"]', 'property', 'og:description', ogDesc);
  setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', b.brand_name);
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', ogTitle);
  setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', ogDesc);
  const ogImg = toAssetUrl(b.og_image_url);
  if (ogImg) {
    setMeta('meta[property="og:image"]', 'property', 'og:image', ogImg);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImg);
  }

  const fav = toAssetUrl(b.favicon_url);
  if (fav) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.head.appendChild(link);
    }
    // cache-bust so browsers pick up the new favicon without a redeploy
    link.setAttribute('href', `${fav}${fav.includes('?') ? '&' : '?'}v=${Date.now()}`);
  }
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULTS);

  const refreshBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/platform/branding`);
      const merged = { ...DEFAULTS, ...res.data };
      setBranding(merged);
      applyDocumentBranding(merged);
    } catch {
      applyDocumentBranding(DEFAULTS);
    }
  }, []);

  useEffect(() => { refreshBranding(); }, [refreshBranding]);

  const assetUrl = useCallback((url) => toAssetUrl(url), []);

  return (
    <BrandingContext.Provider value={{ branding, assetUrl, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};
